if (!customElements.get('td-tabs')) {
  /** Controls tab selection, scrolling, and accessible panel state. */
  class TdTabs extends HTMLElement {
    /** @returns {void} */
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.TABS_WRAPPER = this.querySelector('[data-tabs-wrapper]');
      this.TRIGGERS = this.querySelectorAll('[data-id]');
      this.TAB_SCROLL_LEFT = this.querySelector('[data-scroll-tab-left]');
      this.TAB_SCROLL_RIGHT = this.querySelector('[data-scroll-tab-right]');
      this.ANIMATED_UNDERLINE = this.querySelector('[data-animated-underline]');
      this.activeTab = this.querySelector('[data-id][aria-selected="true"]') || this.TRIGGERS[0];
      this.activeTabContent = document.getElementById(this.activeTab?.dataset?.id);
      if (!this.ANIMATED_UNDERLINE || !this.activeTab || !this.activeTabContent) {
        return;
      }

      this.setActiveTab();
      this.setActiveTabNavArrows();
      this.attachListeners();
    }

    /** @returns {void} */
    disconnectedCallback() {
      this.abortController?.abort();
    }

    /** @returns {void} */
    attachListeners() {
      const listenerOptions = { signal: this.abortController.signal };

      this.TRIGGERS.forEach((trigger) => {
        // Handle mouse clicks
        trigger.addEventListener('click', () => {
          this.activeTab = trigger;
          this.setActiveTab();
        }, listenerOptions);

        // Handle keyboard activation (Enter/Space)
        trigger.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.activeTab = trigger;
            this.setActiveTab();
          }
        }, listenerOptions);
      });
      this.TABS_WRAPPER.addEventListener('scroll', () => {
        this.setActiveTabNavArrows();
      }, listenerOptions);
      this.TAB_SCROLL_LEFT.addEventListener('click', () => {
        this.scrollTabs(-100);
      }, listenerOptions);
      this.TAB_SCROLL_RIGHT.addEventListener('click', () => {
        this.scrollTabs(100);
      }, listenerOptions);
      window.addEventListener('resize', () => {
        this.setActiveTab();
        this.setActiveTabNavArrows();
      }, listenerOptions);
      window.addEventListener('td:section-switcher-changed', () => {
        this.setActiveTab();
      }, listenerOptions);
    }

    /** @returns {void} */
    setActiveTab() {
      this.activeTabContent.classList.remove('td-active');
      this.activeTabContent.setAttribute('aria-hidden', 'true');
      this.activeTabContent = document.getElementById(this.activeTab?.dataset?.id);
      this.ANIMATED_UNDERLINE.style.cssText = `
        left: ${this.activeTab.offsetLeft}px;
        width: ${this.activeTab.offsetWidth - 0.5}px;
      `;
      this.activeTabContent.classList.add('td-active');
      this.activeTabContent.setAttribute('aria-hidden', 'false');

      // Update aria-selected attributes
      this.TRIGGERS.forEach(trigger => {
        trigger.classList.remove('td-active');
        trigger.setAttribute('aria-selected', 'false');
      });
      this.activeTab.classList.add('td-active');
      this.activeTab.setAttribute('aria-selected', 'true');

      // Announce tab change to screen readers
      const announcement = document.querySelector('[data-tab-announcement]');
      if (announcement) {
        const tabName = this.activeTab.textContent.trim() || 'tab';
        announcement.textContent = `${tabName} tab selected`;
      }
    }

    /** @returns {void} */
    setActiveTabNavArrows() {
      if (!this.TAB_SCROLL_LEFT || !this.TAB_SCROLL_RIGHT) {
        return;
      }
      if (this.isScrollable()) {
        if (this.isScrolledLeft()) {
          this.TAB_SCROLL_LEFT.classList.remove('td-active');
        } else {
          this.TAB_SCROLL_LEFT.classList.add('td-active');
        }
        
        if (this.isScrolledRight()) {
          this.TAB_SCROLL_RIGHT.classList.remove('td-active');
        } else {
          this.TAB_SCROLL_RIGHT.classList.add('td-active');
        }
      } else {
        this.TAB_SCROLL_LEFT.classList.remove('td-active');
        this.TAB_SCROLL_RIGHT.classList.remove('td-active');
      }
    }

    /** @returns {boolean} */
    isScrollable() {
      return this.TABS_WRAPPER.scrollWidth > this.TABS_WRAPPER.clientWidth;
    }

    /** @returns {boolean} */
    isScrolledLeft() {
      return this.TABS_WRAPPER.scrollLeft === 0;
    }

    /** @returns {boolean} */
    isScrolledRight() {
      const tolerance = 1;
      return Math.abs(this.TABS_WRAPPER.scrollLeft + this.TABS_WRAPPER.clientWidth - this.TABS_WRAPPER.scrollWidth) <= tolerance;
    }

    /**
     * @param {number} scrollAmount - Horizontal distance to scroll in pixels.
     * @returns {void}
     */
    scrollTabs(scrollAmount) {
      const currentScrollPosition = this.TABS_WRAPPER.scrollLeft + scrollAmount;
      if (currentScrollPosition <= 0) {
        this.TABS_WRAPPER.scrollTo({
          left: 0,
          behavior: 'smooth'
        });
      } else if (currentScrollPosition >= (this.TABS_WRAPPER.scrollWidth - this.TABS_WRAPPER.clientWidth)) {
        this.TABS_WRAPPER.scrollTo({
          left: this.TABS_WRAPPER.scrollWidth - this.TABS_WRAPPER.scrollLeft,
          behavior: 'smooth'
        });
      } else {
        this.TABS_WRAPPER.scrollTo({
          left: currentScrollPosition,
          behavior: 'smooth'
        });
      }
    }
  }

  customElements.define('td-tabs', TdTabs);
}
